import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { DashboardPanel } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';

const FRAMES = {
  daf_yomi: 'דף היומי',
  shinun: 'דף השינון',
  meshannim: 'משננים',
};

const CHANNELS = [
  { key: 'web', label: 'פורטל' },
  { key: 'ivr', label: 'טלפון' },
  { key: 'import', label: 'ייבוא' },
];

const DEFAULT_FILTERS = {
  examId: '',
  frame: '',
  channel: '',
  range: 'all',
};

export { DEFAULT_FILTERS as EXAMINEES_DASHBOARD_DEFAULT_FILTERS, FRAMES as EXAMINEES_FRAME_LABELS };

function Count({ n, active, onClick }) {
  const value = n || 0;
  if (!value) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      variant="body2"
      onClick={onClick}
      sx={{
        fontWeight: 700,
        cursor: 'pointer',
        color: active ? 'primary.main' : 'text.primary',
        textDecoration: active ? 'underline' : 'none',
        '&:hover': { color: 'primary.main' },
      }}
    >
      {value}
    </Typography>
  );
}

export default function ExamineesDashboard({ filters, onFiltersChange, frame = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const examId = filters?.examId || '';
  const channel = filters?.channel || '';
  const lockedFrame = String(frame || filters?.frame || '').trim();
  const hasActiveFilter = !!(examId || channel);

  const patchFilters = useCallback((patch) => {
    onFiltersChange?.({
      examId,
      frame: lockedFrame,
      channel,
      range: 'all',
      ...patch,
      frame: lockedFrame,
    });
  }, [examId, channel, lockedFrame, onFiltersChange]);

  const clearFilters = useCallback(() => {
    onFiltersChange?.({
      examId: '',
      frame: lockedFrame,
      channel: '',
      range: 'all',
    });
  }, [lockedFrame, onFiltersChange]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = lockedFrame ? { frame: lockedFrame } : {};
      const { data: res } = await api.get('/american-exams/attempts-dashboard', { params });
      setData(res);
    } catch (err) {
      console.error('שגיאה בטעינת דשבורד נבחנים:', err);
    } finally {
      setLoading(false);
    }
  }, [lockedFrame]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const rows = data?.byExam || [];
  const activeTitle = rows.find((r) => String(r.examId) === examId)?.title;
  const channelLabel = CHANNELS.find((c) => c.key === channel)?.label;
  const subtitleParts = [
    lockedFrame ? FRAMES[lockedFrame] : null,
    activeTitle || null,
    channelLabel || null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length ? subtitleParts.join(' · ') : undefined;

  return (
    <DashboardPanel
      title="נבחנים"
      subtitle={subtitle}
      icon={<DashboardIcon fontSize="small" />}
      color="primary.main"
      loading={loading}
      onRefresh={fetchDashboard}
      defaultOpen
      bodySx={{ p: 0 }}
    >
      {hasActiveFilter && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(21, 101, 192, 0.04)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {activeTitle
              ? `מסונן לפי מבחן: ${activeTitle}${channelLabel ? ` · ${channelLabel}` : ''}`
              : channelLabel
                ? `מסונן לפי ערוץ: ${channelLabel}`
                : 'סינון פעיל'}
          </Typography>
          <Button size="small" variant="outlined" onClick={clearFilters}>
            הצג הכל
          </Button>
        </Box>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, py: 0.75 }}>מבחן</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, py: 0.75, width: 72 }}>נבחנו</TableCell>
            {CHANNELS.map((c) => {
              const activeChannel = !examId && channel === c.key;
              return (
                <TableCell
                  key={c.key}
                  align="center"
                  onClick={() => patchFilters({ examId: '', channel: activeChannel ? '' : c.key })}
                  sx={{
                    fontWeight: 700,
                    py: 0.75,
                    width: 72,
                    cursor: 'pointer',
                    color: activeChannel ? 'primary.main' : 'text.secondary',
                    textDecoration: activeChannel ? 'underline' : 'none',
                  }}
                >
                  {c.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">אין נבחנים</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => {
            const id = String(row.examId || '');
            const active = id === examId;
            return (
              <TableRow
                key={id}
                hover
                selected={active}
                onClick={() => patchFilters({
                  examId: active && !channel ? '' : id,
                  channel: active ? '' : channel,
                })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ py: 0.6 }}>
                  <Box sx={{ fontWeight: active ? 700 : 500, lineHeight: 1.3 }}>{row.title}</Box>
                  {!lockedFrame && (
                    <Typography variant="caption" color="text.secondary">
                      {FRAMES[row.frame] || row.frameLabel || ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.6, fontWeight: 700 }}>
                  {row.uniqueExaminees ?? 0}
                </TableCell>
                {CHANNELS.map((c) => (
                  <TableCell key={c.key} align="center" sx={{ py: 0.6 }}>
                    <Count
                      n={row.channels?.[c.key]}
                      active={active && channel === c.key}
                      onClick={(e) => {
                        e.stopPropagation();
                        const same = active && channel === c.key;
                        patchFilters({
                          examId: same ? '' : id,
                          channel: same ? '' : c.key,
                        });
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </DashboardPanel>
  );
}
